import type { Query } from "@tanstack/react-query";

/**
 * Raised when the ERP backend could not be reached at all, as opposed to
 * returning an application error. Polling and retry behaviour is relaxed
 * for this case instead of hammering a host that cannot answer.
 */
export class FrappeUnreachableError extends Error {
  readonly frappeUnreachable = true;

  constructor(message = "Frappe is unreachable") {
    super(message);
    this.name = "FrappeUnreachableError";
  }
}

/**
 * Checks a marker property rather than `instanceof`, so the check still
 * holds for error objects that have crossed a serialization boundary.
 */
export function isFrappeUnreachable(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { frappeUnreachable?: boolean }).frappeUnreachable === true
  );
}

/**
 * Reads a failed response once and throws the matching error type.
 * Call only when `response.ok` is false.
 */
export async function throwFromResponse(
  response: Response,
  fallbackMessage: string
): Promise<never> {
  let body: { message?: string; frappe_unreachable?: boolean } | null = null;

  try {
    body = await response.json();
  } catch {
    // Body was not JSON; fall through to the generic error.
  }

  if (response.status === 502 && body?.frappe_unreachable === true) {
    throw new FrappeUnreachableError(body.message ?? fallbackMessage);
  }

  throw new Error(body?.message ?? fallbackMessage);
}

/**
 * Slows polling down while the backend is unreachable rather than stopping
 * it. Stopping would strand the query: the machine hooks disable both
 * `refetchOnMount` and `refetchOnWindowFocus`, so nothing else would trigger
 * a fetch. A slow poll recovers on its own once a request succeeds.
 */
export function slowPollWhenUnreachable(normalMs: number, slowMs = 60_000) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (query: Query<any, any, any, any>) =>
    isFrappeUnreachable(query.state.error) ? slowMs : normalMs;
}

/**
 * Suppresses retries for an unreachable backend while leaving normal retry
 * behaviour intact for every other failure.
 */
export function retryUnlessUnreachable(maxRetries = 3) {
  return (failureCount: number, error: unknown) =>
    !isFrappeUnreachable(error) && failureCount < maxRetries;
}